import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Alert, Spinner as BootstrapSpinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faLink, faTrash, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
import useUser from '../../../hooks/useUser';
import { ConfirmationModal } from '../../../components/UnifiedModal';
import { getErrorMessage } from '../../../utils/errorMessage';
import AccountSettingsLoadingState from '../AccountSettingsLoadingState';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  readThroughAccountSettingsCache,
} from '../accountSettingsDataCache';
import './ConnectionSettings.css';

const DISCORD_SERVER_INVITE = 'https://discord.gg/4R9j6T3PN8';

const DEFAULT_DISCORD_USERNAME = 'BotSpot Agent';
const DEFAULT_TELEGRAM_USERNAME = 'BotSpotTradingBot';

type ChannelKey = 'discord' | 'telegram' | 'slack' | 'whatsapp';

interface BotIdentities {
  discordUsername: string;
  telegramUsername: string;
}

interface ChannelStep {
  text: ReactNode;
}

interface ChannelInfo {
  label: string;
  brandIcon: IconDefinition | null;
  botHandle: string;
  connectUrl: string | null;
  steps: ChannelStep[];
}

interface LinkedChannel {
  channel: ChannelKey;
  channelUsername?: string | null;
  channelUserIdHint?: string | null;
  linkedAt?: string | null;
}

interface ChannelInfoResponse {
  discord?: { username?: string | null };
  telegram?: { username?: string | null };
}

interface ChannelsResponse {
  channels?: LinkedChannel[];
}

function buildChannelInfo({ discordUsername, telegramUsername }: BotIdentities): Record<ChannelKey, ChannelInfo> {
  return {
    discord: {
      label: 'Discord',
      brandIcon: faDiscord,
      botHandle: `@${discordUsername}`,
      connectUrl: 'https://discord.com/channels/@me',
      steps: [
        {
          text: (
            <>
              Join Lumiwealth Discord:{' '}
              <a href={DISCORD_SERVER_INVITE} target="_blank" rel="noopener noreferrer">
                {DISCORD_SERVER_INVITE}
              </a>
            </>
          ),
        },
        {
          text: (
            <>
              Message <strong>{discordUsername}</strong> with <code>hi</code> or <code>/start</code>.
            </>
          ),
        },
        {
          text: (
            <>
              Click <strong>Connect BotSpot Account</strong> in the bot reply.
            </>
          ),
        },
        {
          text: (
            <>Approve the connection here.</>
          ),
        },
      ],
    },
    telegram: {
      label: 'Telegram',
      brandIcon: faTelegram,
      botHandle: `@${telegramUsername}`,
      connectUrl: `https://t.me/${telegramUsername}?start=connect`,
      steps: [
        {
          text: (
            <>
              Open Telegram with <strong>@{telegramUsername}</strong> and send <code>/start</code>.
            </>
          ),
        },
        {
          text: (
            <>
              Tap <strong>Connect BotSpot Account</strong> in the bot reply.
            </>
          ),
        },
        {
          text: (
            <>Approve the connection here.</>
          ),
        },
      ],
    },
    slack: {
      label: 'Slack',
      brandIcon: null,
      botHandle: '',
      connectUrl: null,
      steps: [{ text: 'Slack support is coming soon.' }],
    },
    whatsapp: {
      label: 'WhatsApp',
      brandIcon: null,
      botHandle: '',
      connectUrl: null,
      steps: [{ text: 'WhatsApp support is coming soon.' }],
    },
  };
}

const ConnectionSettings = () => {
  const { getApiToken, user } = useUser();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://localhost:8082';
  const channelInfoCacheKey = makeAccountSettingsCacheKey('connection-channel-info', apiBaseUrl);
  const linkedChannelsCacheKey = makeAccountSettingsCacheKey(
    'connections',
    getAccountSettingsUserCacheKey(user),
    apiBaseUrl,
  );
  const cachedBotIdentities = readAccountSettingsCache<BotIdentities>(channelInfoCacheKey);
  const cachedLinkedChannels = readAccountSettingsCache<LinkedChannel[]>(linkedChannelsCacheKey);

  const [linked, setLinked] = useState<LinkedChannel[]>(() => cachedLinkedChannels || []);
  const [loading, setLoading] = useState(() => !cachedLinkedChannels);
  const [error, setError] = useState<string | null>(null);
  const [disconnectingChannel, setDisconnectingChannel] = useState<ChannelKey | null>(null);
  const [pendingDisconnect, setPendingDisconnect] = useState<ChannelKey | null>(null);

  const [botIdentities, setBotIdentities] = useState<BotIdentities>({
    discordUsername: cachedBotIdentities?.discordUsername || DEFAULT_DISCORD_USERNAME,
    telegramUsername: cachedBotIdentities?.telegramUsername || DEFAULT_TELEGRAM_USERNAME,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const nextBotIdentities = await readThroughAccountSettingsCache<BotIdentities>(channelInfoCacheKey, async () => {
          const response = await fetch(`${apiBaseUrl}/channel/info`);
          if (!response.ok) {
            return {
              discordUsername: DEFAULT_DISCORD_USERNAME,
              telegramUsername: DEFAULT_TELEGRAM_USERNAME,
            };
          }
          const data = await response.json() as ChannelInfoResponse;
          return {
            discordUsername: data?.discord?.username || DEFAULT_DISCORD_USERNAME,
            telegramUsername: data?.telegram?.username || DEFAULT_TELEGRAM_USERNAME,
          };
        });
        if (cancelled) return;
        setBotIdentities(nextBotIdentities);
      } catch {
        // Keep default bot handles if the channel info endpoint is unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, channelInfoCacheKey]);

  const CHANNEL_INFO = useMemo(() => buildChannelInfo(botIdentities), [botIdentities]);

  const fetchChannels = useCallback(async (options: { force?: boolean } = {}) => {
    const cached = readAccountSettingsCache<LinkedChannel[]>(linkedChannelsCacheKey);
    if (cached && !options.force) {
      setLinked(cached);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const nextLinkedChannels = await readThroughAccountSettingsCache<LinkedChannel[]>(linkedChannelsCacheKey, async () => {
        const token = await getApiToken();
        const response = await fetch(`${apiBaseUrl}/channels`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to load connections (status ${response.status})`);
        }
        const data = await response.json() as ChannelsResponse;
        return Array.isArray(data.channels) ? data.channels : [];
      }, options);
      setLinked(nextLinkedChannels);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load your linked channels.'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, getApiToken, linkedChannelsCacheKey]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const requestDisconnect = (channel: ChannelKey): void => {
    setPendingDisconnect(channel);
  };

  const cancelDisconnect = (): void => {
    if (disconnectingChannel) return;
    setPendingDisconnect(null);
  };

  const confirmDisconnect = async (): Promise<void> => {
    const channel = pendingDisconnect;
    if (!channel) return;
    setDisconnectingChannel(channel);
    try {
      const token = await getApiToken();
      const response = await fetch(`${apiBaseUrl}/connect/${channel}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error || `Disconnect failed (status ${response.status})`);
      }
      await fetchChannels({ force: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Disconnect failed.'));
    } finally {
      setDisconnectingChannel(null);
      setPendingDisconnect(null);
    }
  };

  const SUPPORTED: ChannelKey[] = ['discord', 'telegram'];

  return (
    <div className="connection-settings">
      <div className="settings-subpage-header connection-settings-header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title connection-settings-title">Third-party Connections</h2>
          <p className="settings-subpage-description connection-settings-description">
            Link chat channels for alerts and agent messages.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {loading ? (
        <AccountSettingsLoadingState
          className="connection-settings-loading"
          label="Loading connections"
          text="Loading connections..."
          variant="section"
        />
      ) : (
        <div className="connection-settings-list">
          {SUPPORTED.map((channel) => {
            const info = CHANNEL_INFO[channel];
            const link = linked.find((ci) => ci.channel === channel);
            const isLinked = Boolean(link);
            const isDisconnecting = disconnectingChannel === channel;

            return (
              <article key={channel} className="connection-settings-channel">
                <div className="connection-settings-channel-row">
                  <div className="connection-settings-channel-left">
                    <div className="connection-settings-channel-name">
                        {info.brandIcon && (
                          <FontAwesomeIcon
                            icon={info.brandIcon}
                            className={`connection-settings-brand-icon connection-settings-brand-icon--${channel}`}
                          />
                        )}
                        {info.label}
                        {isLinked ? (
                          <span className="connection-settings-status connection-settings-status--linked">
                            <FontAwesomeIcon icon={faLink} /> Linked
                          </span>
                        ) : (
                          <span className="connection-settings-status">
                            Not connected
                          </span>
                        )}
                      </div>
                      {link && (
                        <div className="connection-settings-channel-meta">
                          {link.channelUsername ? (
                            <span>@{link.channelUsername}</span>
                          ) : link.channelUserIdHint ? (
                            <span>User ending in ...{link.channelUserIdHint}</span>
                          ) : null}
                          {link.linkedAt && (
                            <span className="connection-settings-channel-meta-sep">
                              Linked {new Date(link.linkedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="connection-settings-channel-right">
                      {isLinked && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => requestDisconnect(channel)}
                          disabled={isDisconnecting}
                        >
                          {isDisconnecting ? (
                            <>
                              <BootstrapSpinner size="sm" animation="border" /> Disconnecting...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faTrash} /> Disconnect
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  {!isLinked && (
                    <div className="connection-settings-connect-panel">
                      <div className="connection-settings-connect-lead">
                        Connect {info.label} as <strong>{info.botHandle || 'the bot'}</strong>.
                      </div>
                      <ol className="connection-settings-steps">
                        {info.steps.map((step, idx) => (
                          <li key={idx}>{step.text}</li>
                        ))}
                      </ol>
                      {info.connectUrl && (
                        <Button
                          variant="primary"
                          size="sm"
                          href={info.connectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="connection-settings-open-btn"
                        >
                          {info.brandIcon && <FontAwesomeIcon icon={info.brandIcon} />}{' '}
                          Open {info.label}{' '}
                          <FontAwesomeIcon icon={faExternalLinkAlt} className="connection-settings-open-btn-ext" />
                        </Button>
                      )}
                    </div>
                  )}
              </article>
            );
          })}
        </div>
      )}

      <ConfirmationModal
        isOpen={Boolean(pendingDisconnect)}
        onClose={cancelDisconnect}
        onConfirm={confirmDisconnect}
        title={`Disconnect ${pendingDisconnect ? (CHANNEL_INFO[pendingDisconnect]?.label || pendingDisconnect) : ''}?`}
        message={
          pendingDisconnect
            ? `This will unlink ${CHANNEL_INFO[pendingDisconnect]?.label || pendingDisconnect} from your BotSpot account. The bot will no longer respond to DMs from this account until you connect it again.`
            : ''
        }
        confirmText="Disconnect"
        cancelText="Cancel"
        variant="danger"
        isProcessing={Boolean(disconnectingChannel)}
      />
    </div>
  );
};

export default ConnectionSettings;
